import { theme, Spinner, EmptyState } from '@ketch-com/deck'
import { useDataSentryContext } from 'pages/dataSentry/context/DataSentryContext'
import { Box, Typography } from '@mui/material'
import { ControlStatus as SystemsControlStatus } from '@ketch-com/windlass/dist/dataSentry/v1/systems/systems'
import { SemiCircle, SemiCircleProps } from 'components/SemiCircle'

export const ConsentControl: React.FC = () => {
  const { systemsSummary, isSystemsLoading: isSystemsSummaryLoading } = useDataSentryContext()

  const totalUncontrolledSystems =
    systemsSummary?.controlTypes?.find(system => system.status === SystemsControlStatus.UNCONTROLLED) || {}
  const totalControlledSystems =
    systemsSummary?.controlTypes?.find(system => system.status === SystemsControlStatus.CONTROLLED) || {}
  const totalSystems = (totalControlledSystems?.count || 0) + (totalUncontrolledSystems?.count || 0)
  const percentage =
    totalControlledSystems?.count && totalSystems ? Math.round((totalControlledSystems?.count / totalSystems) * 100) : 0
  const progressSize = Math.round(percentage / 2) || 0

  const semiCircleProps: SemiCircleProps = {
    containerWidth: 360,
    containerHeight: 180,
    size: 340,
    value: progressSize,
    thickness: 6,
    top: 8,
    left: 12,
  }

  const hasNoData = systemsSummary?.controlTypes?.length === 0

  return (
    <Box
      flexBasis={360}
      display="flex"
      flexDirection="column"
      gap={2}
      sx={{ backgroundColor: theme.palette.Common.Container }}
      borderRadius="11px"
    >
      <Box px={2.5} py={2}>
        <Typography pb={1} variant="h4">
          Systems Consent Control
        </Typography>
      </Box>

      {isSystemsSummaryLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '350px' }}>
          <Spinner size="32px" thickness={2} />
        </Box>
      ) : null}

      {!isSystemsSummaryLoading && hasNoData ? (
        <Box display="flex" flexDirection="column" justifyContent="flex-end" gap={8} flex={1}>
          <Box px={2.5} display="flex" flexDirection="column" alignItems="center" gap={2} textAlign="center">
            <EmptyState
              size="small"
              colorTheme={theme.palette.Black.o48}
              iconName="FPie"
              title="No Data"
              subTitle="Review your settings and connections, or wait for the scan to finish processing."
            />
          </Box>

          <SemiCircle {...semiCircleProps} />
        </Box>
      ) : null}

      {!isSystemsSummaryLoading && !hasNoData ? (
        <Box display="flex" flexDirection="column" justifyContent="flex-end" gap={8} flex={1}>
          <Box px={2.5} display="flex" flexDirection="column" alignItems="center" gap={2} textAlign="center">
            <Typography variant="h1">{percentage}%</Typography>
            <Typography variant="label">
              {totalControlledSystems?.count || 0}/
              <Typography variant="label" color={theme.palette.Text.Secondary}>
                {totalSystems}
              </Typography>{' '}
              Systems Controlled
            </Typography>
            <Typography variant="smallBody" color={theme.palette.Text.Secondary}>
              Connect all systems to ensure consent is properly propagated across vendor platforms, which can help
              improve your score.
            </Typography>
          </Box>

          <SemiCircle {...semiCircleProps} />
        </Box>
      ) : null}
    </Box>
  )
}
